/**
 * Saving a document back to iManage, in its two steps. `prepareIManageSave`
 * asks the server to record an approval for these exact bytes and this exact
 * destination; nothing is written. `confirmIManageSave` sends the same bytes
 * with that approval, and only then does the server write. A failed or
 * ambiguous confirmation is never retried here: the server keeps its outcome
 * and the person is told to check iManage.
 *
 * Used by the tracked-edits review and by "Save as new version".
 *
 * @module components/apps/imanage/imanageSave
 */
import { IManageEntry } from "@amoreg/contracts/imanage";
import * as Schema from "effect/Schema";

import { bytesToBase64, imanageRequest } from "../../../state/imanage";

const Prepared = Schema.Struct({ id: Schema.String, hash: Schema.String, expires: Schema.Number });

export interface IManageSaveTarget {
  readonly libraryId: string;
  /** The document the file was reviewed from (its revision is re-checked). */
  readonly documentId: string;
  readonly revision: string | null;
  readonly mode: "version" | "document";
  /** Destination folder for a new document; ignored for a new version. */
  readonly folderId: string;
  readonly name: string;
}

export interface PreparedIManageSave {
  readonly id: string;
  readonly hash: string;
  readonly expires: number;
  /** The exact bytes approved, base64; confirmation must send these. */
  readonly content: string;
  readonly target: IManageSaveTarget;
}

export async function prepareIManageSave(
  organizationId: string,
  target: IManageSaveTarget,
  bytes: Uint8Array,
): Promise<PreparedIManageSave> {
  const content = bytesToBase64(bytes);
  const result = Schema.decodeUnknownSync(Prepared)(
    await imanageRequest(organizationId, "prepare-save", {}, { ...target, content }),
  );
  return { ...result, content, target };
}

export async function confirmIManageSave(
  organizationId: string,
  prepared: Pick<PreparedIManageSave, "id" | "content">,
): Promise<IManageEntry> {
  return Schema.decodeUnknownSync(IManageEntry)(
    await imanageRequest(
      organizationId,
      "save",
      {},
      { intentId: prepared.id, content: prepared.content },
    ),
  );
}

/** A reviewed file's name in iManage: the name without a trailing `.docx`, trimmed. */
export const saveName = (name: string) => name.replace(/\.docx$/i, "").trim();
